import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import * as Highcharts from "highcharts";
import { SVGRendererSymbolPlus } from "@app/components/hull-condition/hull-condition-tracks/series-builder-config";
import HC_more from "highcharts/highcharts-more";
import MapModule from "highcharts/modules/map";
import * as moment from "moment";
import { CiiRanges } from "@app/services/api/api.models";

@Component({
  selector: "app-cii-rating-tracks",
  templateUrl: "./cii-rating-tracks.component.html",
  styleUrls: ["./cii-rating-tracks.component.scss"],
})
export class CiiRatingTracksComponent implements OnInit, OnChanges {
  @Input() ciiTracks: any;
  @Input() ciiRanges: CiiRanges;
  @Input() availableHistoryYears: number[];
  @Input() selectedYear: number;
  localCiiTracks: any[] = [];
  Highcharts = Highcharts;
  ciiOptions: any = {};

  chartRef!: Highcharts.Chart;

  private chartConfig = {
    chart: {
      type: "line",
      zoomType: "x",
      style: {
        fontFamily: "Heebo",
      },
    },
    legend: {
      itemStyle: {
        fontWeight: "bold",
      },
      itemHiddenStyle: {
        color: "#cccccc",
        textDecoration: "none",
      },
    },
    title: {
      text: "",
    },
    tooltip: {
      borderWidth: 1,
    },
  };

  private yAxisConfig = {
    yAxis: {
      title: {
        text: "AER [gCO₂/tnm]",
        style: {
          "font-weight": "bold",
          fill: "#000000",
          color: "#000000",
        },
      },
      labels: {
        align: "right",
        x: -8,
        style: {
          color: "#666666",
        },
      },
      gridLineWidth: 1,
      tickColor: "#ccd6eb",
      lineColor: "#ccd6eb",
      lineWidth: 1,
    },
  };

  private xAxisConfig = {
    xAxis: {
      title: {
        text: "",
      },
      labels: {
        style: {
          color: "#666666",
        },
        formatter: function () {
          const date = new Date(this.value);
          return `${date.toLocaleString("en-GB", { month: "short" })}. '${date.getFullYear().toString().slice(-2)}`;
        },
      },
      lineColor: "#ccd6eb",
      type: "datetime",
      gridLineWidth: 1,
      tickColor: "#ccd6eb",
    },
  };
  constructor() {
    Highcharts.SVGRenderer.prototype.symbols.plus = SVGRendererSymbolPlus;
    HC_more(Highcharts);
    MapModule(Highcharts);
  }
  ngOnInit(): void {
    this.handleCiiTracksChange(this.ciiTracks);
    this.handleAvailableHistoryYearsChange(this.availableHistoryYears);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["ciiTracks"] || (changes["selectedYear"] && !changes["selectedYear"].firstChange)) {
      this.handleCiiTracksChange(this.ciiTracks);
    }
    if (changes["availableHistoryYears"]) {
      this.handleAvailableHistoryYearsChange(this.availableHistoryYears);
    }
  }

  private getSeriesFromTracks(tracks) {
    const styles = {
      showInLegend: true,
      index: 2,
      legendIndex: 1,
      marker: {
        symbol: "circle",
        enabled: true,
      },
      events: {
        legendItemClick: function (e) {
          e.preventDefault();
        },
      },
      lineWidth: 1,
      dashStyle: "Dot",
      color: "#e3e3e3",
      states: {
        hover: {
          lineWidthPlus: 0,
        },
      },
    };

    return {
      series: [
        {
          name: "AER [gCO₂/tnm]",
          ...styles,
          data: tracks?.map((track) => this.trackMeasurementToSeriesData(track)),
        },
      ],
    };
  }

  private trackMeasurementToSeriesData(trackMeasurement) {
    const value = trackMeasurement.rollingAer_gCO2_per_tnm;
    const ranges = this.ciiRanges;

    if (!ranges) return { x: trackMeasurement.datetime_utc, y: value };

    const cappedValue = value > Math.round(ranges.e[1] * 10) / 10 ? Math.round(ranges.e[1] * 10) / 10 : value;
    const color = this.getColorByValue(value);

    return {
      x: this.convertToUtc(trackMeasurement.datetime_utc),
      y: cappedValue,
      marker: {
        fillColor: color,
        radius: 2.5,
      },
      custom: {
        originalValue: value,
      },
    };
  }

  convertToUtc(dateString: string): Date {
    const localDate = new Date(dateString);
    const utcTimestamp = Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localDate.getHours(),
      localDate.getMinutes(),
      localDate.getSeconds()
    );
    return new Date(utcTimestamp);
  }

  private getColorByValue(value: number): string {
    const ranges = this.ciiRanges;

    if (!ranges) return "#404040";

    if (value < ranges.a[1]) return "#9BC56D";
    if (value < ranges.b[1]) return "#C6DEAB";
    if (value < ranges.c[1]) return "#FDE399";
    if (value < ranges.d[1]) return "#EE9DA3";
    if (value < ranges.e[1]) return "#E1535D";
    return "#404040";
  }

  private handleCiiTracksChange(tracks: any) {
    this.localCiiTracks = tracks;
    this.buildChartOptions();
  }

  private handleAvailableHistoryYearsChange(availableHistoryYears: any) {
    if (!availableHistoryYears || !availableHistoryYears.length) {
      return;
    }

    function sortYearsAscending(years: number[]): number[] {
      return years.sort((a, b) => a - b);
    }

    this.availableHistoryYears = sortYearsAscending(availableHistoryYears);
  }

  public buildChartOptions() {
    const ciiTracks = this.localCiiTracks;

    const yearStart = moment.utc({ year: this.selectedYear, month: 0, day: 1 }).valueOf();
    const yearEnd =
      this.selectedYear === moment().year()
        ? moment().endOf("day").valueOf()
        : moment.utc({ year: this.selectedYear, month: 11, day: 31, hour: 23, minute: 59, second: 59 }).valueOf();
    const ranges = this.ciiRanges;

    if (!ranges) return;

    const baseTicksM = [0, 2, 4, 6, 8, 10].map((m) => moment(yearStart).add(m, "months"));
    const endClashes = baseTicksM.some((t) => t.isSame(moment(yearEnd), "month"));
    const tickPositions = [...baseTicksM.map((t) => t.valueOf()), ...(endClashes ? [] : [moment(yearEnd).valueOf()])];

    const xAxisConfigForYear = {
      xAxis: {
        ...this.xAxisConfig.xAxis,
        min: yearStart,
        max: yearEnd,
        tickPositions: tickPositions,
      },
    };

    this.ciiOptions = {
      ...this.chartConfig,
      ...this.yAxisConfig,
      ...xAxisConfigForYear,
      tooltip: {
        formatter: function () {
          const original = this.point.custom?.originalValue ?? this.y;
          const date = new Date(this.x);
          const formattedDate = date.toLocaleDateString("en-GB", {
            weekday: "long",
            day: "2-digit",
            month: "short",
            year: "numeric",
          });
          return `<b>${formattedDate}</b><br/>AER: ${original.toFixed(2)} gCO₂/tnm`;
        },
      },
      yAxis: {
        ...this.yAxisConfig.yAxis,

        min: ranges.a[0],
        max: ranges.e[1],
        tickPositions: [
          Math.round(ranges.a[0] * 10) / 10,
          Math.round(ranges.a[1] * 10) / 10,
          Math.round(ranges.b[1] * 10) / 10,
          Math.round(ranges.c[1] * 10) / 10,
          Math.round(ranges.d[1] * 10) / 10,
          Math.round(ranges.e[1] * 10) / 10,
        ],
        labels: {
          formatter: function () {
            return this.value.toFixed(1);
          },
        },
      },
      ...this.getSeriesFromTracks(ciiTracks),
    };
  }
}
